<script setup>
/* Offene Zugangsanfragen — Liste zum Annehmen oder Ablehnen.
 *
 * Annehmen = einladen: "Annehmen und einladen" schickt die Supabase-Mail und setzt die
 * gewählte Rolle (Edge Function invite-user). Angezeigt werden pending und approved;
 * approved sind Altfälle, die früher nur angenommen, nie eingeladen wurden.
 *
 * Sass bis 2026-08-26 als aufklappbarer Kasten auf der Startseite — dort stand
 * er dauerhaft im Weg, obwohl er meistens leer war. Jetzt liegt er in der
 * Nutzerverwaltung; dass es etwas zu entscheiden gibt, meldet stattdessen ein
 * Hinweis am Konto-Knopf in der Kopfleiste.
 *
 * Der Zustand kommt aus useAccessRequests (Singleton) — dieselbe Zahl wie am
 * Konto-Knopf, ohne zweiten Ladevorgang.
 */

import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccessRequests } from '../composables/useAccessRequests.js';
import { fmtDateTime } from '../domain/format.js';
import { requestDisplayName } from '../domain/accessRequest.js';
import { identityLines } from '../domain/identity.js';
import { useConfirm } from '../composables/useConfirm.js';
import { INVITE_ROLES, DEFAULT_INVITE_ROLE } from '../domain/invitations.js';

const { t } = useI18n();
const { pending, loading, error, start, decide, invite } = useAccessRequests();
const { confirm } = useConfirm();

const busyId = ref(null);
const failed = ref(null);
/* Gewählte Rolle je Anfrage; ohne Eintrag gilt der Standard (Gast). */
const roleById = ref({});
/* Name (Vor- + Nachname) und Adresse; ist der Name leer, steht "kein Name" darüber, ist der Name
   gleich der Adresse, steht sie nur einmal (domain/identity.js). */
const linesOf = (r) => identityLines(requestDisplayName(r), r.email, { emptyTitle: t('accessRequests.noName') });
const roleOf = (r) => roleById.value[r.id] || DEFAULT_INVITE_ROLE;

/* Fehlercode → Schlüssel unter errors.*. Unbekanntes bleibt allgemein. */
const ERROR_KEY = {
  forbidden: 'adminOnly',
  notAuthenticated: 'adminOnly',
  notConnected: 'notConnected',
  saveFailed: 'saveFailed',
  alreadyRegistered: 'alreadyRegistered',
  notInvitable: 'notInvitable',
  roleNotSet: 'roleNotSet',
  emailRateLimit: 'emailRateLimit',
};
const errorKey = (e) => ERROR_KEY[e?.message] || 'inviteFailed';

async function onInvite(request) {
  busyId.value = request.id;
  failed.value = null;
  try {
    await invite(request.id, roleOf(request));
  } catch (e) {
    failed.value = errorKey(e);
  } finally {
    busyId.value = null;
  }
}

async function onReject(request) {
  const ok = await confirm({
    title: t('confirm.rejectRequestTitle'),
    message: t('confirm.rejectRequest', { name: requestDisplayName(request) || request.email }),
    confirmLabel: t('accessRequests.reject'),
  });
  if (!ok) return;

  busyId.value = request.id;
  failed.value = null;
  try {
    await decide(request.id, 'rejected');
  } catch (e) {
    failed.value = errorKey(e);
  } finally {
    busyId.value = null;
  }
}

onMounted(start);
</script>

<template>
  <div class="rq-list">
    <div v-if="loading" class="rq-empty">{{ $t('common.loading') }}</div>
    <div v-else-if="error" class="rq-empty error">{{ $t(`errors.${error}`) }}</div>
    <div v-else-if="!pending.length" class="rq-empty">{{ $t('accessRequests.empty') }}</div>

    <div v-for="r in pending" :key="r.id" class="rq-row">
      <div class="rq-info">
        <div class="rq-name">{{ linesOf(r).title }}</div>
        <div v-if="r.organization" class="rq-org">{{ r.organization }}</div>
        <div v-if="linesOf(r).subtitle" class="rq-email">{{ linesOf(r).subtitle }}</div>
        <div v-if="r.created_at" class="rq-when">{{ fmtDateTime(r.created_at) }}</div>
      </div>
      <div class="rq-actions">
        <select
          class="rq-role"
          :aria-label="$t('accessRequests.roleLabel')"
          :value="roleOf(r)"
          :disabled="busyId === r.id"
          @change="roleById[r.id] = $event.target.value"
        >
          <option v-for="role in INVITE_ROLES" :key="role" :value="role">
            {{ $t(`roles.${role}`) }}
          </option>
        </select>
        <button
          class="rq-btn done"
          :disabled="busyId === r.id"
          @click="onInvite(r)"
        >
          {{ r.status === 'approved' ? $t('accessRequests.invite') : $t('accessRequests.approveInvite') }}
        </button>
        <button
          class="rq-btn reject"
          :disabled="busyId === r.id"
          @click="onReject(r)"
        >
          {{ $t('accessRequests.reject') }}
        </button>
      </div>
    </div>

    <p v-if="failed" class="rq-failed">{{ $t(`errors.${failed}`) }}</p>

    <!-- Annehmen verschickt die Einladung sofort (Edge Function invite-user). -->
    <p class="rq-hint">{{ $t('accessRequests.approveHint') }}</p>
  </div>
</template>

<style scoped>
.rq-list {
  margin: -4px -4px 0;
}
.rq-row {
  padding: 12px 4px;
}
.rq-org {
  font-size: var(--fs-xs);
  color: var(--muted);
  margin-top: 2px;
}
.rq-when {
  font-size: var(--fs-2xs);
  color: var(--dim);
  margin-top: 3px;
}
.rq-empty {
  padding: 14px 4px;
}
.rq-empty.error {
  color: var(--red);
}
.rq-failed {
  padding: 8px 4px 0;
  font-size: var(--fs-sm);
  color: var(--red);
}
.rq-hint {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border-soft);
  font-size: var(--fs-xs);
  color: var(--dim);
  line-height: 1.6;
}
.rq-role {
  font-size: var(--fs-xs);
  background: var(--bg);
  color: var(--text);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 6px 8px;
}
.rq-role:disabled {
  opacity: 0.5;
}
.rq-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
</style>
